import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
    Box,
    Container,
    Typography,
    Avatar,
    Chip,
    Divider,
    Stack,
    CardMedia,
    CircularProgress,
    IconButton,
    useTheme,
    useMediaQuery,
    Grid
} from '@mui/material';
import {
    Share,
    FacebookOutlined,
} from '@mui/icons-material';

import { useGetBlogDetailsQuery, useLazyListBlogsQuery } from "store/services/publicApi";
import { LandingFooter } from 'layouts/LandingPage/LandingFooter';

import { formatDate } from "utils/dateFormats";

import BlogCard from "./BlogCard";
import NavBar from 'components/navbar';

const DEFAULT_IMAGE = 'https://www.pay4freight.com/wp-content/uploads/2022/06/Benefits-of-a-Transport-Management-System-for-Owner-Operators.png';

const BlogDetailScreen = () => {

    const navigate = useNavigate();

    const { slug } = useParams();

    const { data: blogData, error: blogError, isLoading: blogLoading } = useGetBlogDetailsQuery(slug);

    const [fetchBlogs, { data: blogsData, isLoading: blogsLoading }] = useLazyListBlogsQuery();

    const [blog, setBlog] = useState(null);

    const [relatedPosts, setRelatedPosts] = useState([]);

    const [categories, setCategories] = useState([]);

    const theme = useTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    React.useEffect(() => {
        if (blogData) {
            setBlog(blogData);

            if (blogData.category) {
                fetchBlogs({
                    active: 'true',
                    category: blogData.category.name,
                    limit: 2,
                });
            }
            fetchBlogs({
                active: 'true',
                limit: 100,
            }).then((response) => {
                if (response.data) {
                    const uniqueCategories = [
                        ...new Set(response.data.data.map((post) => post.category?.name))
                    ].map((name) => ({ name }));
                    setCategories(uniqueCategories);
                }
            });
        }
    }, [blogData, fetchBlogs]);

    React.useEffect(() => {
        if (blogsData) {
            setRelatedPosts(blogsData.data);
        }
    }, [blogsData]);

    const handleTagClick = () => {
        navigate(`/blogs`);
    };

    if (blogLoading || blogsLoading) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: theme.palette.background.default
            }}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    if (blogError) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: theme.palette.background.default
            }}>
                <Typography variant="h6" color="error">
                    Failed to load the blog details. Please try again later.
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <NavBar />
            {blog && (
                <Container maxWidth="lg" sx={{ mt: 10, mb: 2 }}>
                    <Grid container spacing={10}>
                        {/* Main Content Column */}
                        <Grid item md={8}>
                            <Typography
                                variant={isMobile ? "h5" : "h4"}
                                fontWeight={600}
                                sx={{
                                    mb: 3,
                                    lineHeight: 1.2,
                                    color: theme.palette.text.primary
                                }}
                            >
                                {blog.title}
                            </Typography>

                            {/* Blog Thumbnail */}
                            <Box sx={{
                                position: 'relative',
                                borderRadius: 2,
                                overflow: 'hidden',
                                boxShadow: 3,
                                mb: 4
                            }}>
                                <CardMedia
                                    component="img"
                                    image={blog?.featureImage?.[0]?.url || DEFAULT_IMAGE}
                                    alt={blog.title}
                                    sx={{
                                        height: isMobile ? 250 : 450,
                                        objectFit: 'cover',
                                        transition: 'transform 0.3s ease',
                                        '&:hover': {
                                            transform: 'scale(1.02)'
                                        }
                                    }}
                                />
                            </Box>

                            {/* Blog Meta Information */}
                            <Box sx={{
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                justifyContent: 'space-between',
                                alignItems: isMobile ? 'flex-start' : 'center',
                                mb: 4
                            }}>
                                {/* Author Info */}
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: isMobile ? 2 : 0
                                }}>
                                    <Avatar
                                        src={blog.image?.[0]?.url}
                                        alt={blog.author}
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            mr: 2,
                                            border: `2px solid crimson`
                                        }}
                                    />
                                    <Stack spacing={0.5}>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {blog.author}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDate(blog.createdAt)}
                                        </Typography>
                                    </Stack>
                                </Box>

                                {/* Action Buttons */}
                                <Stack direction="row" spacing={1}>
                                    <IconButton>
                                        <FacebookOutlined />
                                    </IconButton>
                                    <IconButton aria-label="share">
                                        <Share />
                                    </IconButton>
                                </Stack>
                            </Box>

                            {/* Category Chip */}
                            {blog.category?.name && (
                                <Chip
                                    label={blog.category.name}
                                    size="medium"
                                    sx={{
                                        bgcolor: "crimson",
                                        color: theme.palette.primary.contrastText,
                                        mb: 3,
                                        fontSize: '0.9rem',
                                        px: 2,
                                        py: 1.5
                                    }}
                                />
                            )}

                            {/* Blog Content */}
                            <Box sx={{
                                '& p': {
                                    fontSize: '1.1rem',
                                    lineHeight: 1.8,
                                    mb: 3,
                                    color: theme.palette.text.primary
                                },
                                '& h2': {
                                    mt: 4,
                                    mb: 2,
                                    fontWeight: 600
                                },
                                '& img': {
                                    maxWidth: '100%',
                                    height: 'auto',
                                    borderRadius: 1,
                                    my: 3
                                }
                            }}>
                                <div dangerouslySetInnerHTML={{ __html: blog.content }} />
                            </Box>
                            {/* Tags Section */}
                            {blog.tags && blog.tags.length > 0 && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                                        Tags:
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                        {blog.tags.map((tag, index) => (
                                            <Chip
                                                key={index}
                                                label={`#${tag}`}
                                                onClick={() => handleTagClick(tag)}
                                                clickable
                                                sx={{
                                                    bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
                                                    color: theme.palette.text.primary,
                                                    fontSize: '0.9rem',
                                                    px: 1.5,
                                                    '&:hover': {
                                                        bgcolor: 'crimson',
                                                        color: 'white',
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {/* Expanded Topics Section */}
                            <Box sx={{ mt: 5 }}>
                                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                                    Explore More Topics
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                                    {categories.map((category, index) => (
                                        <Chip
                                            key={index}
                                            label={category.name}
                                            size="medium"
                                            clickable
                                            sx={{
                                                bgcolor: (theme) =>
                                                    theme.palette.mode === 'dark'
                                                        ? theme.palette.grey[800]
                                                        : theme.palette.grey[200],
                                                color: (theme) => theme.palette.text.primary,
                                                fontSize: '0.9rem',
                                                px: 2,
                                                '&:hover': {
                                                    bgcolor: 'crimson',
                                                    color: 'white',
                                                },
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Box>

                            {/* Divider */}
                            <Divider sx={{ my: 5 }} />

                            {/* Simplified Author Bio */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: theme.palette.mode === 'dark' ?
                                    theme.palette.grey[900] :
                                    theme.palette.grey[100],
                                p: 3,
                                borderRadius: 2,
                                mb: 4
                            }}>
                                <Avatar
                                    src={blog.image?.[0].url || ''}
                                    alt={blog.author}
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        mr: 3,
                                        border: `3px solid crimson`
                                    }}
                                />
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                        About {blog.author}
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        {blog.authorBio || 'The author is a passionate writer who loves to share insights and knowledge on various topics.'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Right Sidebar Column */}
                        <Grid item md={4}>
                            <Box sx={{ mt: 5 }}>
                                <Typography variant="h6" fontWeight={700}>
                                   Related Posts
                                </Typography>
                                <Grid container spacing={3} sx={{ mt: 2,  }}>
                                    {relatedPosts?.slice(0, 4).map((post) => (
                                        <Grid item xs={12} key={post?._id}>
                                            <BlogCard post={post} />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            )}
            <LandingFooter />
        </>
    );
};

export default BlogDetailScreen;